import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  uploadFile, scanReceipt, fetchReceipts, fetchProjects, assignReceipt,
  fetchDocuments, saveDocument, deleteDocument, fetchAbandonedQuotes, analyzeDocumentRemote,
  fetchQuotes, supabase,
} from "./lib/supabase";
import COLORS from "./config/colors";

const DOCUMENT_TYPES = [
  { id: "invoice", icon: "receipt", label: "Invoices", desc: "Past invoices — AI learns your line items, formatting, terms, and pricing" },
  { id: "estimate", icon: "estimate", label: "Estimates & Bids", desc: "Past estimates — AI learns how you present pricing to customers" },
  { id: "job_code", icon: "tag", label: "Job Codes", desc: "Your job code system — maps codes to customers, budgets, cost categories" },
  { id: "contract", icon: "contract", label: "Contracts", desc: "Standard contract template — AI uses the language for future docs" },
  { id: "material_list", icon: "wood", label: "Supplier Pricing", desc: "Real supplier price sheets — AI uses actual costs for better estimates" },
];

const DOC_ICONS = {
  receipt: "\u{1F4C4}", estimate: "\u{1F4B0}", tag: "\u{1F3F7}\uFE0F",
  contract: "\u{1F4DD}", wood: "\u{1FAB5}", camera: "\u{1F4F8}",
};

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = () => reject(new Error("File read failed"));
    reader.readAsDataURL(file);
  });
}

export default function AdminPanel() {
  const [activeTab, setActiveTab] = useState("receipts");
  const [documents, setDocuments] = useState([]);
  const [abandoned, setAbandoned] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [portfolioPhotos, setPortfolioPhotos] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [uploadType, setUploadType] = useState(null);
  const [showLearned, setShowLearned] = useState(null);
  const [scanningFile, setScanningFile] = useState(null);
  const [assigningReceipt, setAssigningReceipt] = useState(null);
  const fileInputRef = useRef(null);
  const receiptInputRef = useRef(null);

  // Load data on mount
  useEffect(() => {
    fetchDocuments().then(setDocuments);
    fetchAbandonedQuotes().then(setAbandoned);
    fetchReceipts().then(setReceipts);
    fetchProjects().then(setProjects);
    fetchQuotes({ completed: true }).then(setQuotes);
    // Load portfolio photos from storage bucket
    loadPortfolioPhotos();
  }, []);

  const loadPortfolioPhotos = async () => {
    if (!supabase) return;
    const { data } = await supabase.storage
      .from('photos')
      .list('photo', { limit: 50, sortBy: { column: 'created_at', order: 'desc' } });
    if (data?.length > 0) {
      const photos = data
        .filter(f => f.name && !f.name.startsWith('.'))
        .map(f => {
          const { data: urlData } = supabase.storage.from('photos').getPublicUrl(`photo/${f.name}`);
          return { name: f.name, path: `photo/${f.name}`, url: urlData?.publicUrl, created: f.created_at };
        });
      setPortfolioPhotos(photos);
    }
  };

  const handlePortfolioUpload = async (e) => {
    const files = Array.from(e.target.files);
    setUploadingPhotos(true);
    for (const file of files) {
      const path = `photo/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
      await uploadFile('photos', path, file);
    }
    await loadPortfolioPhotos();
    setUploadingPhotos(false);
    e.target.value = "";
  };

  const deletePortfolioPhoto = async (photo) => {
    if (!supabase) return;
    await supabase.storage.from('photos').remove([photo.path]);
    setPortfolioPhotos(prev => prev.filter(p => p.path !== photo.path));
  };

  const handleReceiptUpload = useCallback(async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      setScanningFile(file.name);
      try {
        const base64 = await fileToBase64(file);
        const result = await scanReceipt(base64, file.type, file.name, null, null);
        if (result?.receipt) {
          setReceipts(prev => [result.receipt, ...prev]);
        }
      } catch (err) {
        console.error("Receipt scan failed:", err);
      }
    }
    setScanningFile(null);
    e.target.value = "";
  }, []);

  // Build combined job list from projects + quotes
  const jobList = useMemo(() => [
    ...projects.map(p => ({
      id: p.id,
      type: "project",
      jobCode: p.job_code,
      label: `${p.job_code} — ${p.project_name}`,
      name: p.customer_name,
    })),
    ...quotes
      .filter(q => !projects.some(p => p.quote_id === q.id))
      .map(q => {
        const lastName = (q.customer_name || "UNKNOWN").split(" ").pop().toUpperCase().slice(0, 6);
        const code = `${lastName}-${String(q.deck_sqft || 0)}`;
        return {
          id: q.id,
          type: "quote",
          jobCode: code,
          label: `${code} — ${q.customer_name} (${q.material_name || "TBD"}, ${q.deck_sqft || "?"}sqft)`,
          name: q.customer_name,
        };
      }),
  ], [projects, quotes]);

  const handleAssignReceipt = useCallback(async (receiptId, jobId) => {
    const job = jobList.find(j => j.id === jobId);
    if (!job) return;
    if (job.type === "project") {
      const updated = await assignReceipt(receiptId, job.id, job.jobCode);
      if (updated) {
        setReceipts(prev => prev.map(r => r.id === receiptId ? updated : r));
      }
    } else {
      // Quote-based job — assign with generated job code (no project_id yet)
      const updated = await assignReceipt(receiptId, null, job.jobCode);
      if (updated) {
        setReceipts(prev => prev.map(r => r.id === receiptId ? { ...updated, job_code: job.jobCode } : r));
      }
    }
    setAssigningReceipt(null);
  }, [jobList]);

  const handleDocUpload = useCallback(async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      const filePath = `${uploadType}/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
      const url = await uploadFile("documents", filePath, file);
      const doc = await saveDocument({
        doc_type: uploadType,
        file_name: file.name,
        file_url: url || filePath,
        file_type: file.type.includes("pdf") ? "pdf" : file.type.startsWith("image/") ? "image" : "other",
        analyzed: false,
      });
      if (doc) {
        setDocuments(prev => [doc, ...prev]);

        // Trigger Claude analysis (non-blocking) — not for photos
        fileToBase64(file).then(base64 => {
          analyzeDocumentRemote(doc.id, base64, file.type, uploadType).then(result => {
            if (result?.success) {
              setDocuments(prev => prev.map(d =>
                d.id === doc.id
                  ? { ...d, analyzed: true, learned_context: result.learned_context, analysis: result.analysis, formatting_style: result.formatting_style }
                  : d
              ));
            }
          }).catch(err => console.error("Analysis failed:", err));
        });
      }
    }
    e.target.value = "";
    setUploadType(null);
  }, [uploadType]);

  // Retry analysis for pending documents
  const retryAnalysis = useCallback(async (doc) => {
    if (!doc.file_url) return;
    setDocuments(prev => prev.map(d => d.id === doc.id ? { ...d, analyzing: true } : d));
    try {
      // Fetch the file from storage and convert to base64
      const response = await fetch(doc.file_url);
      const blob = await response.blob();
      const base64 = await fileToBase64(blob);
      const result = await analyzeDocumentRemote(doc.id, base64, blob.type || "application/octet-stream", doc.doc_type);
      if (result?.success) {
        setDocuments(prev => prev.map(d =>
          d.id === doc.id
            ? { ...d, analyzed: true, analyzing: false, learned_context: result.learned_context, analysis: result.analysis, formatting_style: result.formatting_style }
            : d
        ));
      } else {
        setDocuments(prev => prev.map(d => d.id === doc.id ? { ...d, analyzing: false } : d));
      }
    } catch (err) {
      console.error("Retry analysis failed:", err);
      setDocuments(prev => prev.map(d => d.id === doc.id ? { ...d, analyzing: false } : d));
    }
  }, []);

  const styles = {
    panel: { fontFamily: "'DM Sans', sans-serif", color: COLORS.slate, maxWidth: 1100, margin: "0 auto" },
    tabs: { display: "flex", gap: 4, marginBottom: 24, borderBottom: `2px solid rgba(155,142,130,0.12)`, paddingBottom: 0 },
    tab: (active) => ({ padding: "12px 20px", border: "none", borderBottom: `3px solid ${active ? COLORS.honey : "transparent"}`, background: "none", color: active ? COLORS.bark : COLORS.stone, fontWeight: active ? 700 : 500, fontSize: 14, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", transition: "all 0.2s", marginBottom: -2 }),
    card: { background: COLORS.white, borderRadius: 14, padding: 20, boxShadow: "0 1px 4px rgba(44,24,16,0.06)", border: "1px solid rgba(155,142,130,0.12)", marginBottom: 16 },
    label: { fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5, color: COLORS.stone, marginBottom: 6 },
    sectionTitle: { fontSize: 20, fontWeight: 700, fontFamily: "'Playfair Display', serif", color: COLORS.bark, marginBottom: 4 },
    btn: { padding: "10px 20px", borderRadius: 10, border: "none", background: COLORS.honey, color: COLORS.white, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" },
    btnOutline: { padding: "10px 20px", borderRadius: 10, border: `2px solid ${COLORS.honey}`, background: "transparent", color: COLORS.honey, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" },
    btnSmall: { padding: "6px 14px", borderRadius: 8, border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" },
  };

  // ─── RECEIPTS & JOB COSTING TAB ───
  const renderReceipts = () => {
    const totalUnassigned = receipts.filter(r => r.status === "pending").length;
    const totalCost = receipts.reduce((s, r) => s + Number(r.total || 0), 0);

    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <h3 style={styles.sectionTitle}>Receipts & Job Costing</h3>
            <p style={{ color: COLORS.stone, fontSize: 13 }}>
              Scan supplier receipts. Claude extracts line items and updates your pricing model automatically.
            </p>
          </div>
          <div>
            <button onClick={() => receiptInputRef.current?.click()} style={styles.btn}>
              {scanningFile ? `Scanning ${scanningFile}...` : "Scan Receipt"}
            </button>
            <input ref={receiptInputRef} type="file" multiple accept="image/*,.pdf" onChange={handleReceiptUpload} style={{ display: "none" }} />
          </div>
        </div>

        {/* Summary cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 20 }}>
          <div style={{ ...styles.card, textAlign: "center" }}>
            <div style={styles.label}>Total Receipts</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: COLORS.bark, fontFamily: "'Playfair Display', serif" }}>{receipts.length}</div>
          </div>
          <div style={{ ...styles.card, textAlign: "center" }}>
            <div style={styles.label}>Unassigned</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: totalUnassigned > 0 ? COLORS.honey : COLORS.green, fontFamily: "'Playfair Display', serif" }}>{totalUnassigned}</div>
          </div>
          <div style={{ ...styles.card, textAlign: "center" }}>
            <div style={styles.label}>Total AP</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: COLORS.bark, fontFamily: "'Playfair Display', serif" }}>${totalCost.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
          </div>
        </div>

        {/* Receipt list */}
        {receipts.length === 0 && !scanningFile && (
          <div style={{ ...styles.card, textAlign: "center", padding: 48, color: COLORS.stone }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>📸</div>
            <p style={{ fontSize: 15 }}>No receipts yet. Photograph a supplier invoice to get started.</p>
            <p style={{ fontSize: 13 }}>Claude Vision will extract every line item, calculate per-sqft costs, and feed it into your pricing model.</p>
          </div>
        )}

        {scanningFile && (
          <div style={{ ...styles.card, display: "flex", alignItems: "center", gap: 12, background: "rgba(196,145,59,0.04)" }}>
            <div style={{ width: 20, height: 20, border: "3px solid", borderColor: `${COLORS.honey} transparent transparent transparent`, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
            <div>
              <div style={{ fontWeight: 600, color: COLORS.bark }}>Scanning {scanningFile}...</div>
              <div style={{ fontSize: 12, color: COLORS.stone }}>Claude is extracting line items, costs, and pricing data</div>
            </div>
          </div>
        )}

        {receipts.map(receipt => (
          <div key={receipt.id} style={styles.card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: 12 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: COLORS.bark }}>{receipt.supplier_name || "Unknown Supplier"}</div>
                <div style={{ fontSize: 12, color: COLORS.stone }}>
                  {receipt.receipt_number && `#${receipt.receipt_number} · `}
                  {receipt.receipt_date || "No date"} · {receipt.category}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.bark }}>${Number(receipt.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                <span style={{
                  display: "inline-block", padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600,
                  background: receipt.status === "assigned" ? "rgba(74,124,89,0.08)" : "rgba(196,145,59,0.08)",
                  color: receipt.status === "assigned" ? COLORS.green : COLORS.honey,
                }}>
                  {receipt.status === "assigned" ? `${receipt.job_code}` : "Unassigned"}
                </span>
              </div>
            </div>

            {/* Line items */}
            {receipt.line_items?.length > 0 && (
              <div style={{ background: COLORS.cream, borderRadius: 10, padding: 12, marginBottom: 12 }}>
                <div style={{ ...styles.label, marginBottom: 8 }}>Line Items ({receipt.line_items.length})</div>
                {receipt.line_items.map((item, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 13, borderBottom: i < receipt.line_items.length - 1 ? "1px solid rgba(155,142,130,0.1)" : "none" }}>
                    <span style={{ color: COLORS.bark }}>{item.description}</span>
                    <span style={{ fontWeight: 600 }}>
                      {item.quantity} {item.unit} × ${Number(item.unit_cost || 0).toFixed(2)} = ${Number(item.total || 0).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Pricing updates badge */}
            {receipt.pricing_updates?.length > 0 && (
              <div style={{ padding: "8px 12px", borderRadius: 8, background: "rgba(59,124,196,0.06)", border: "1px solid rgba(59,124,196,0.15)", fontSize: 12, color: COLORS.blue, marginBottom: 12 }}>
                Pricing model updated: {receipt.pricing_updates.map(u => u.material).join(", ")}
              </div>
            )}

            {/* Assign to project */}
            {receipt.status === "pending" && (
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {assigningReceipt === receipt.id ? (
                  <>
                    <select
                      onChange={(e) => e.target.value && handleAssignReceipt(receipt.id, e.target.value)}
                      style={{ flex: 1, padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(155,142,130,0.3)", fontSize: 13, fontFamily: "'DM Sans', sans-serif", background: COLORS.white }}
                      defaultValue=""
                    >
                      <option value="" disabled>Select a job...</option>
                      {jobList.length === 0 && <option disabled>No jobs or quotes yet</option>}
                      {projects.length > 0 && <optgroup label="Active Projects">
                        {jobList.filter(j => j.type === "project").map(j => (
                          <option key={j.id} value={j.id}>{j.label}</option>
                        ))}
                      </optgroup>}
                      {quotes.length > 0 && <optgroup label="From Quotes">
                        {jobList.filter(j => j.type === "quote").map(j => (
                          <option key={j.id} value={j.id}>{j.label}</option>
                        ))}
                      </optgroup>}
                    </select>
                    <button onClick={() => setAssigningReceipt(null)} style={{ ...styles.btnSmall, background: COLORS.cream, color: COLORS.stone }}>Cancel</button>
                  </>
                ) : (
                  <button onClick={() => setAssigningReceipt(receipt.id)} style={{ ...styles.btnSmall, background: COLORS.honey, color: COLORS.white }}>
                    Assign to Job
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // ─── DOCUMENTS TAB ───
  const renderDocuments = () => (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h3 style={styles.sectionTitle}>Upload Business Documents</h3>
        <p style={{ color: COLORS.stone, fontSize: 13, marginBottom: 16 }}>
          Upload your existing invoices, estimates, job codes, and contracts. The AI will analyze each document to learn your formatting style, pricing patterns, and business vocabulary.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
          {DOCUMENT_TYPES.map(type => (
            <button key={type.id} onClick={() => { setUploadType(type.id); fileInputRef.current?.click(); }} style={{ padding: 16, borderRadius: 12, border: "2px dashed rgba(155,142,130,0.25)", background: COLORS.cream, cursor: "pointer", textAlign: "left", fontFamily: "'DM Sans', sans-serif", transition: "all 0.15s" }}>
              <div style={{ fontSize: 24, marginBottom: 6 }}>{DOC_ICONS[type.icon]}</div>
              <div style={{ fontWeight: 600, fontSize: 13, color: COLORS.bark, marginBottom: 4 }}>{type.label}</div>
              <div style={{ fontSize: 11, color: COLORS.stone, lineHeight: 1.4 }}>{type.desc}</div>
            </button>
          ))}
        </div>
        <input ref={fileInputRef} type="file" multiple onChange={handleDocUpload} style={{ display: "none" }} accept=".pdf,.docx,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.heic" />
      </div>

      <div>
        {(() => { const docs = documents.filter(d => d.doc_type !== "photo"); return (<>
        <h3 style={styles.sectionTitle}>Uploaded Documents ({docs.length})</h3>
        {docs.length === 0 ? (
          <div style={{ ...styles.card, textAlign: "center", padding: 36, color: COLORS.stone }}>
            No documents uploaded yet. Upload invoices, estimates, or contracts above.
          </div>
        ) : docs.map(doc => {
          const typeInfo = DOCUMENT_TYPES.find(t => t.id === doc.doc_type);
          return (
            <div key={doc.id} style={{ ...styles.card, display: "flex", alignItems: "center", gap: 16, position: "relative" }}>
              <div style={{ fontSize: 28 }}>{typeInfo ? DOC_ICONS[typeInfo.icon] : "\u{1F4C4}"}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: COLORS.bark }}>{doc.file_name}</div>
                <div style={{ fontSize: 12, color: COLORS.stone }}>
                  {typeInfo?.label || doc.doc_type} · {new Date(doc.created_at).toLocaleDateString()}
                </div>
              </div>
              {doc.analyzed && doc.learned_context && (
                <button onClick={() => setShowLearned(showLearned === doc.id ? null : doc.id)} style={{ ...styles.btnOutline, padding: "6px 14px", fontSize: 12 }}>
                  {showLearned === doc.id ? "Hide" : "View"} Learned Data
                </button>
              )}
              {!doc.analyzed && !doc.analyzing && (
                <button onClick={() => retryAnalysis(doc)} style={{ ...styles.btnSmall, background: COLORS.honey, color: COLORS.white }}>
                  Retry Analysis
                </button>
              )}
              {doc.analyzing && (
                <span style={{ fontSize: 12, color: COLORS.honey, fontWeight: 600 }}>Analyzing...</span>
              )}
              <span style={{
                padding: "6px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                background: doc.analyzed ? "rgba(74,124,89,0.08)" : "rgba(196,145,59,0.08)",
                color: doc.analyzed ? COLORS.green : COLORS.honey,
              }}>
                {doc.analyzed ? "Analyzed" : doc.analyzing ? "Analyzing" : "Pending"}
              </span>
              <button
                onClick={async () => {
                  if (!window.confirm(`Delete "${doc.file_name}"?`)) return;
                  const ok = await deleteDocument(doc.id, doc.file_url);
                  if (ok) setDocuments(prev => prev.filter(d => d.id !== doc.id));
                }}
                style={{ ...styles.btnSmall, background: "transparent", color: COLORS.red, border: `1px solid ${COLORS.red}` }}
              >
                Delete
              </button>
              {showLearned === doc.id && doc.learned_context && (
                <div style={{ position: "absolute", right: 20, top: "100%", zIndex: 10, width: 400, padding: 16, background: COLORS.white, borderRadius: 12, border: `1px solid rgba(155,142,130,0.15)`, boxShadow: "0 8px 24px rgba(0,0,0,0.1)", fontSize: 13, color: COLORS.bark, lineHeight: 1.6 }}>
                  <div style={{ fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: 1, color: COLORS.honey, marginBottom: 8 }}>What the AI learned</div>
                  {doc.learned_context}
                </div>
              )}
            </div>
          );
        })}
      </>); })()}
      </div>
    </div>
  );

  // ─── PORTFOLIO PHOTOS TAB ───
  const portfolioInputRef = useRef(null);
  const renderPortfolio = () => (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h3 style={styles.sectionTitle}>Portfolio Photos</h3>
          <p style={{ color: COLORS.stone, fontSize: 13 }}>
            These photos cascade on the public site background. Add your best completed project shots.
          </p>
        </div>
        <div>
          <button onClick={() => portfolioInputRef.current?.click()} disabled={uploadingPhotos} style={styles.btn}>
            {uploadingPhotos ? "Uploading..." : "+ Add Photos"}
          </button>
          <input ref={portfolioInputRef} type="file" multiple accept="image/*" onChange={handlePortfolioUpload} style={{ display: "none" }} />
        </div>
      </div>

      {portfolioPhotos.length === 0 && !uploadingPhotos && (
        <div style={{ ...styles.card, textAlign: "center", padding: 48, color: COLORS.stone }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>📸</div>
          <p style={{ fontSize: 15 }}>No portfolio photos yet.</p>
          <p style={{ fontSize: 13 }}>Upload completed project photos — they'll cascade across the public site background.</p>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12 }}>
        {portfolioPhotos.map(photo => (
          <div key={photo.path} style={{ position: "relative", borderRadius: 12, overflow: "hidden", border: "1px solid rgba(155,142,130,0.15)", background: COLORS.white }}>
            <div style={{ paddingTop: "75%", position: "relative" }}>
              <img src={photo.url} alt={photo.name} style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
            <div style={{ padding: "8px 10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 11, color: COLORS.stone, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "70%" }}>{photo.name}</span>
              <button
                onClick={() => { if (window.confirm("Delete this photo?")) deletePortfolioPhoto(photo); }}
                style={{ background: "none", border: "none", color: COLORS.red, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // ─── ABANDONED QUOTES TAB ───
  const renderAbandoned = () => (
    <div>
      <h3 style={styles.sectionTitle}>Abandoned Quotes</h3>
      <p style={{ color: COLORS.stone, fontSize: 13, marginBottom: 20 }}>
        People who started the quote builder but didn't finish. Pulled live from the database.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 20 }}>
        {[
          { label: "Total Incomplete", value: abandoned.length, color: COLORS.honey },
          { label: "Email Captured", value: abandoned.filter(a => a.customer_email).length, color: COLORS.green },
          { label: "Has Phone", value: abandoned.filter(a => a.customer_phone).length, color: COLORS.blue },
        ].map((s, i) => (
          <div key={i} style={{ ...styles.card, textAlign: "center" }}>
            <div style={styles.label}>{s.label}</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: s.color, fontFamily: "'Playfair Display', serif" }}>{s.value}</div>
          </div>
        ))}
      </div>

      <div style={styles.card}>
        {abandoned.length === 0 ? (
          <div style={{ textAlign: "center", padding: 24, color: COLORS.stone }}>No abandoned quotes found.</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid rgba(155,142,130,0.1)" }}>
                {["Started", "Progress", "Size", "Material", "Contact", "Status"].map(h => (
                  <th key={h} style={{ ...styles.label, padding: "8px 8px", textAlign: "left", fontSize: 10 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {abandoned.map(a => (
                <tr key={a.id} style={{ borderBottom: "1px solid rgba(155,142,130,0.08)" }}>
                  <td style={{ padding: "12px 8px", fontSize: 12, color: COLORS.stone }}>{new Date(a.created_at).toLocaleString()}</td>
                  <td style={{ padding: "12px 8px" }}>
                    <div style={{ display: "flex", gap: 3 }}>
                      {[0, 1, 2, 3].map(step => (
                        <div key={step} style={{ width: 20, height: 6, borderRadius: 3, background: step <= (a.step_reached || 0) ? COLORS.honey : "rgba(155,142,130,0.15)" }} />
                      ))}
                    </div>
                    <div style={{ fontSize: 10, color: COLORS.stone, marginTop: 2 }}>Step {(a.step_reached || 0) + 1}/4</div>
                  </td>
                  <td style={{ padding: "12px 8px", fontWeight: 600, fontSize: 13 }}>{a.deck_sqft ? `${a.deck_sqft} sqft` : "\u2014"}</td>
                  <td style={{ padding: "12px 8px", fontSize: 12 }}>{a.material_name || "\u2014"}</td>
                  <td style={{ padding: "12px 8px" }}>
                    {a.customer_email ? (
                      <div>
                        <div style={{ fontSize: 12, color: COLORS.blue, fontWeight: 600 }}>{a.customer_email}</div>
                        {a.customer_phone && <div style={{ fontSize: 11, color: COLORS.stone }}>{a.customer_phone}</div>}
                      </div>
                    ) : (
                      <span style={{ fontSize: 11, color: COLORS.red, fontStyle: "italic" }}>No email</span>
                    )}
                  </td>
                  <td style={{ padding: "12px 8px" }}>
                    <span style={{
                      fontSize: 11, padding: "4px 8px", borderRadius: 6, fontWeight: 600,
                      background: a.followup_sent ? "rgba(74,124,89,0.08)" : "rgba(155,142,130,0.08)",
                      color: a.followup_sent ? COLORS.green : COLORS.stone,
                    }}>
                      {a.followup_sent ? "Follow-up sent" : a.customer_email ? "Pending follow-up" : "No contact"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );

  return (
    <div style={styles.panel}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        button:hover { filter: brightness(1.05); }
      `}</style>

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: COLORS.bark, color: COLORS.honey, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>{"\u2699\uFE0F"}</div>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, fontFamily: "'Playfair Display', serif", color: COLORS.bark, margin: 0 }}>Admin Panel</h2>
          <div style={{ fontSize: 12, color: COLORS.stone }}>Owner Dashboard</div>
        </div>
      </div>

      <div style={styles.tabs}>
        {[
          { id: "receipts", label: "\u{1F4F8} Receipts & Costs" },
          { id: "portfolio", label: "\u{1F4F7} Portfolio Photos" },
          { id: "documents", label: "\u{1F4C4} Documents & Learning" },
          { id: "abandoned", label: "\u23F3 Abandoned Quotes" },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={styles.tab(activeTab === tab.id)}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "receipts" && renderReceipts()}
      {activeTab === "portfolio" && renderPortfolio()}
      {activeTab === "documents" && renderDocuments()}
      {activeTab === "abandoned" && renderAbandoned()}
    </div>
  );
}
